import React, { useState, useEffect } from 'react';
import { Repository, PullRequest } from '../types';
import { Modal, Button } from './ui';

interface ExemptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (exemptPRs: string[], branch?: string) => Promise<void>;
  repository: Repository | null;
  branch?: string; // Optional: specific branch to configure exemptions for
}

export default function ExemptionModal({
  isOpen,
  onClose,
  onSave,
  repository,
  branch,
}: ExemptionModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pull Requests state
  const [pullRequests, setPullRequests] = useState<PullRequest[]>([]);
  const [loadingPRs, setLoadingPRs] = useState(false);
  const [selectedPRs, setSelectedPRs] = useState<Set<number>>(new Set());
  const [savedExemptPRs, setSavedExemptPRs] = useState<Set<number>>(new Set());
  const [prSearchQuery, setPrSearchQuery] = useState('');
  const [selectedAuthorFilter, setSelectedAuthorFilter] = useState<string>('');

  useEffect(() => {
    if (repository && isOpen) {
      if (branch) {
        fetchExemptions();
      } else {
        const frozenBranch = repository.frozenBranches?.find(b => b.frozen);
        const initialPRs = new Set((frozenBranch?.exemptPRs || []).map(pr => parseInt(pr, 10)));
        setSelectedPRs(initialPRs);
        setSavedExemptPRs(initialPRs);
      }

      setPrSearchQuery('');
      setSelectedAuthorFilter('');
      setError('');

      fetchPullRequests();
    }
  }, [repository, isOpen, branch]);

  const fetchExemptions = async () => {
    if (!repository) return;

    try {
      const url = branch
        ? `/api/v1/repos/${repository.owner}/${repository.repo}/exemptions?branch=${encodeURIComponent(branch)}`
        : `/api/v1/repos/${repository.owner}/${repository.repo}/exemptions`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch exemptions');
      }
      const data = await response.json();
      const initialPRs = new Set((data.exemptPRs || []).map((pr: string) => parseInt(pr, 10)));
      setSelectedPRs(initialPRs);
      setSavedExemptPRs(initialPRs);
    } catch (err) {
      console.error('Error fetching exemptions:', err);
      setError('Failed to load exemptions. Please try again.');
    }
  };

  const fetchPullRequests = async () => {
    if (!repository) return;

    setLoadingPRs(true);
    try {
      const response = await fetch(
        `/api/v1/repos/${repository.owner}/${repository.repo}/pull-requests`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch pull requests');
      }
      const data = await response.json();
      // If branch specified, filter PRs to only those targeting that branch
      const allPRs = data.pullRequests || [];
      setPullRequests(allPRs);
    } catch (err) {
      console.error('Error fetching pull requests:', err);
      setError('Failed to load pull requests. Please try again.');
    } finally {
      setLoadingPRs(false);
    }
  };

  const handleSave = async () => {
    setError('');

    // Convert selected PR numbers to string array
    const exemptPRs = Array.from(selectedPRs).map(num => num.toString());

    setSaving(true);
    try {
      await onSave(exemptPRs, branch);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save exemptions');
    } finally {
      setSaving(false);
    }
  };

  const togglePR = (prNumber: number) => {
    const newSelected = new Set(selectedPRs);
    if (newSelected.has(prNumber)) {
      newSelected.delete(prNumber);
    } else {
      newSelected.add(prNumber);
    }
    setSelectedPRs(newSelected);
  };

  const filteredPRs = pullRequests
    .filter(pr => {
      const matchesSearch =
        prSearchQuery === '' ||
        pr.title.toLowerCase().includes(prSearchQuery.toLowerCase()) ||
        pr.number.toString().includes(prSearchQuery);

      const matchesAuthor = selectedAuthorFilter === '' || pr.user.login === selectedAuthorFilter;

      return matchesSearch && matchesAuthor;
    })
    .sort((a, b) => {
      const aExempt = savedExemptPRs.has(a.number);
      const bExempt = savedExemptPRs.has(b.number);
      if (aExempt && !bExempt) return -1;
      if (!aExempt && bExempt) return 1;
      return b.number - a.number;
    })
    .slice(0, 8);

  const uniqueAuthors = Array.from(new Set(pullRequests.map(pr => pr.user.login))).sort();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        <h3>PR Exemptions{branch ? ` (${branch})` : ''}</h3>
      </Modal.Header>
      <Modal.Body>
        {/* PR exemptions */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
            }}
          >
            Exempt Pull Requests
          </label>

          {/* PR Search and Filter */}
          <div style={{ marginBottom: '8px' }}>
            <input
              type="text"
              placeholder="Search by title or number..."
              value={prSearchQuery}
              onChange={e => setPrSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                marginBottom: '6px',
              }}
            />
            <select
              value={selectedAuthorFilter}
              onChange={e => setSelectedAuthorFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                background: '#fff',
              }}
            >
              <option value="">All authors</option>
              {uniqueAuthors.map(author => (
                <option key={author} value={author}>
                  {author}
                </option>
              ))}
            </select>
          </div>

          {/* PR List */}
          <div
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              maxHeight: '300px',
              overflowY: 'auto',
              background: '#fff',
            }}
          >
            {loadingPRs ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Loading pull requests...
              </div>
            ) : filteredPRs.length === 0 ? (
              <div
                style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}
              >
                {pullRequests.length === 0
                  ? 'No open pull requests'
                  : 'No pull requests match your filters'}
              </div>
            ) : (
              filteredPRs.map(pr => (
                <label
                  key={pr.number}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '10px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: selectedPRs.has(pr.number) ? '#f0f9ff' : 'transparent',
                  }}
                  onMouseEnter={e => {
                    if (!selectedPRs.has(pr.number)) {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!selectedPRs.has(pr.number)) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedPRs.has(pr.number)}
                    onChange={() => togglePR(pr.number)}
                    style={{
                      marginTop: '2px',
                      cursor: 'pointer',
                    }}
                  />
                  <img
                    src={pr.user.avatar_url}
                    alt={pr.user.login}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#111827',
                        marginBottom: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      #{pr.number} {pr.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>by {pr.user.login}</div>
                  </div>
                </label>
              ))
            )}
          </div>

          <p
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: '#6b7280',
            }}
          >
            {selectedPRs.size === 0
              ? 'No PRs selected'
              : `${selectedPRs.size} PR${selectedPRs.size === 1 ? '' : 's'} selected`}
          </p>
        </div>

        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '14px',
              marginTop: '12px',
            }}
          >
            <i className="fa-solid fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
            {error}
          </div>
        )}

        <div
          style={{
            background: '#f3f4f6',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#4b5563',
            marginTop: '16px',
          }}
        >
          <i
            className="fa-solid fa-info-circle"
            style={{ marginRight: '8px', color: '#3b82f6' }}
          ></i>
          <strong>Note:</strong> Exceptions only apply when the repository is frozen. During normal
          operation, all PRs follow standard merge rules.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
